import {
  ASSESSMENT_SECTIONS,
  COMMITMENT_OPTIONS,
  DECLARATIONS,
  ROLE_COMMITMENTS,
  SUPPORT_PRIORITIES,
} from './content.js';
import { renderQuestionControl } from './controls.js';

function element(tagName, options = {}, children = []) {
  const node = document.createElement(tagName);
  if (options.className) node.className = options.className;
  if (options.text !== undefined) node.textContent = options.text;
  for (const [name, value] of Object.entries(options.attributes || {})) {
    if (value !== null && value !== undefined) node.setAttribute(name, String(value));
  }
  for (const child of children) if (child) node.append(child);
  return node;
}

function labelFor(options, id, fallback = 'Not recorded') {
  return options.find((option) => option.id === id)?.label || fallback;
}

function statusLabel(status) {
  return ({ complete: 'Complete', attention: 'Needs attention', not_started: 'Not started' })[status]
    || 'Not started';
}

function summary(state) {
  return element('section', { className: 'review-summary', attributes: { 'aria-labelledby': 'review-summary-title' } }, [
    element('div', { className: 'review-summary__head' }, [
      element('div', {}, [
        element('h2', { text: 'Your assessment at a glance', attributes: { id: 'review-summary-title' } }),
        element('p', { text: 'Check every section before the final submission step. You can still edit anything until you submit.' }),
      ]),
    ]),
    element('ol', {}, ASSESSMENT_SECTIONS.map((section) => {
      const status = state.sectionStatuses?.[section.id] || 'not_started';
      const edit = element('button', {
        className: 'review-summary__edit',
        text: 'Edit',
        attributes: { type: 'button', 'aria-label': `Edit ${section.title}` },
      });
      edit.addEventListener('click', () => state.onNavigate(section.id));
      return element('li', { className: 'review-summary__row' }, [
        element('span', { className: 'review-summary__number', text: String(section.number).padStart(2, '0') }),
        element('strong', { text: section.title }),
        element('span', {
          className: `status-${status}`,
          text: statusLabel(status),
          attributes: { 'data-review-status': section.id },
        }),
        edit,
      ]);
    })),
  ]);
}

function commitments(answers) {
  const values = answers['reality.commitments'] || {};
  return element('section', { className: 'review-details', attributes: { 'aria-labelledby': 'review-commitments-title' } }, [
    element('h2', { text: 'Role commitments', attributes: { id: 'review-commitments-title' } }),
    element('dl', {}, ROLE_COMMITMENTS.map((commitment) => {
      const answer = values[commitment.id] || {};
      return element('div', { className: 'review-details__row' }, [
        element('dt', { text: commitment.statement }),
        element('dd', { text: [
          labelFor(COMMITMENT_OPTIONS, answer.choice),
          answer.explanation || '',
        ].filter(Boolean).join(' ') }),
      ]);
    })),
  ]);
}

function availability(answers) {
  const leaveText = answers['review.planned-leave-details'] || 'Not recorded';
  const priorities = (answers['review.support-priorities'] || [])
    .map((id) => labelFor(SUPPORT_PRIORITIES, id, id))
    .join('; ') || 'Not recorded';
  const rows = [
    ['Earliest acting duties', answers['review.acting-date'] || 'Not recorded'],
    ['Planned leave before 1 September', leaveText],
    ['Travel or rota constraints', answers['review.travel-rota-constraints'] || 'None recorded'],
    ['Support priorities', priorities],
  ];
  return element('section', { className: 'review-details', attributes: { 'aria-labelledby': 'review-availability-title' } }, [
    element('h2', { text: 'Availability and support', attributes: { id: 'review-availability-title' } }),
    element('dl', {}, rows.map(([term, value]) => element('div', { className: 'review-details__row' }, [
      element('dt', { text: term }),
      element('dd', { text: value }),
    ]))),
  ]);
}

function confirmationDialog(state) {
  if (!state.confirmationOpen) return null;
  const typed = element('input', {
    className: 'assessment-input',
    attributes: {
      id: 'submit-confirmation',
      type: 'text',
      autocomplete: 'off',
    },
  });
  const confirm = element('button', {
    className: 'assessment-button assessment-button--primary',
    text: 'Confirm submission',
    attributes: { type: 'button', disabled: '' },
  });
  const declarationsConfirmed = () => DECLARATIONS.every(({ id }) => (
    state.answers['review.declarations']?.[id] === true
  ));
  typed.addEventListener('input', () => {
    confirm.disabled = typed.value === 'SUBMIT' && declarationsConfirmed() ? false : true;
  });
  confirm.addEventListener('click', state.onSubmit);
  const cancel = element('button', {
    className: 'assessment-button assessment-button--secondary',
    text: 'Keep editing',
    attributes: { type: 'button' },
  });
  cancel.addEventListener('click', state.onCloseConfirmation);
  requestAnimationFrame(() => typed.focus());
  const backdrop = element('button', {
    className: 'submission-modal__backdrop',
    attributes: { type: 'button', 'aria-label': 'Close submission confirmation', tabindex: '-1' },
  });
  backdrop.addEventListener('click', state.onCloseConfirmation);
  const dialog = element('div', {
    className: 'submission-modal',
    attributes: {
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'submission-modal-title',
      'aria-describedby': 'submission-modal-description',
    },
  }, [
    element('p', { className: 'assessment-eyebrow', text: 'FINAL CHECK' }),
    element('h2', { text: `Submit ${state.candidate.firstName}'s assessment?`, attributes: { id: 'submission-modal-title' } }),
    element('p', {
      text: 'Your answers will lock after submission and cannot be changed.',
      attributes: { id: 'submission-modal-description' },
    }),
    element('label', { className: 'question-label', text: 'Type SUBMIT to confirm', attributes: { for: 'submit-confirmation' } }),
    typed,
    state.submissionError ? element('p', { className: 'assessment-error', text: state.submissionError, attributes: { role: 'alert' } }) : null,
    element('div', { className: 'submission-modal__actions' }, [cancel, confirm]),
  ]);
  return element('div', { className: 'submission-modal-layer' }, [backdrop, dialog]);
}

export function renderReview(section, state) {
  return element('div', { className: 'review-layout' }, [
    summary(state),
    commitments(state.answers),
    availability(state.answers),
    element('div', { className: 'assessment-questions' }, section.questions.map((question) =>
      renderQuestionControl({
        question,
        value: state.answers[question.id],
        error: state.errors[question.id],
        onChange: state.onChange,
      }))),
    confirmationDialog(state),
  ]);
}
